/* eslint-disable react/prop-types */
/*
  SendEmailDialog — compose + send candidate profile to client.
  - Pre-fills subject + body template with candidate name/title
  - Recruiter can edit subject + body before sending
  - Attaches the PDF by default
  - Shows recent email logs per resume
*/
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, Send, CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function SendEmailDialog({ open, onOpenChange, resume }) {
  const [form, setForm] = useState({ to_email: "", cc: "", subject: "", body_html: "", attach_pdf: true });
  const [sending, setSending] = useState(false);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    if (!open || !resume) return;
    const name = resume.data?.name || resume.title || "candidate";
    const title = resume.data?.title || "";
    setForm({
      to_email: resume.client_name || "",
      cc: "",
      subject: `Candidate submission — ${name}${title ? ` · ${title}` : ""}`,
      body_html: `<p>Hi team,</p>\n<p>Please find attached the profile of <strong>${name}</strong>${title ? ` (${title})` : ""}.</p>\n<p>Highlights:</p>\n<ul>${(resume.data?.highlights || []).slice(0, 3).map((h) => `<li><strong>${h.title || ""}</strong>: ${h.description || ""}</li>`).join("")}</ul>\n<p>Happy to set up an intro call. Let me know your earliest availability this week.</p>\n<p>Best regards,<br/>ResumeForge Team</p>`,
      attach_pdf: true,
    });
    api.get(`/email/logs/${resume.id}`).then((r) => setLogs(r.data || [])).catch(() => {});
  }, [open, resume]);

  const send = async () => {
    if (!form.to_email) return toast.error("Enter a recipient email");
    setSending(true);
    try {
      await api.post(`/email/send/${resume.id}`, form);
      toast.success("Email sent successfully");
      const r = await api.get(`/email/logs/${resume.id}`);
      setLogs(r.data || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed — check SMTP config"); }
    finally { setSending(false); }
  };

  if (!resume) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto" data-testid="send-email-dialog">
        <DialogHeader>
          <DialogTitle>Send candidate profile</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs font-semibold">To</Label>
              <Input value={form.to_email} onChange={(e) => setForm({ ...form, to_email: e.target.value })} placeholder="client@company.com" data-testid="email-to" />
            </div>
            <div>
              <Label className="text-xs font-semibold">CC (optional)</Label>
              <Input value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })} placeholder="manager@..." data-testid="email-cc" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-semibold">Subject</Label>
            <Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} data-testid="email-subject" />
          </div>
          <div>
            <Label className="text-xs font-semibold">Body (HTML supported)</Label>
            <Textarea rows={10} value={form.body_html} onChange={(e) => setForm({ ...form, body_html: e.target.value })} className="font-mono text-[12px]" data-testid="email-body" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={form.attach_pdf} onCheckedChange={(v) => setForm({ ...form, attach_pdf: !!v })} data-testid="email-attach-pdf" />
            Attach resume as PDF
          </label>

          {logs.length > 0 && (
            <div className="border-t border-border pt-3">
              <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Recent email activity</div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {logs.map((l) => (
                  <div key={l.id} className="text-xs flex items-center gap-2" data-testid={`email-log-${l.id}`}>
                    {l.status === "sent" ? <CheckCircle2 className="size-3.5 text-emerald-500 shrink-0" />
                      : l.status === "failed" ? <XCircle className="size-3.5 text-rose-500 shrink-0" />
                      : <Loader2 className="size-3.5 animate-spin shrink-0" />}
                    <span className="font-semibold">{l.to_email}</span>
                    <span className="text-muted-foreground truncate">{l.subject}</span>
                    <span className="ml-auto text-muted-foreground">{new Date(l.sent_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={send} disabled={sending} className="gap-2" data-testid="email-send-button">
            {sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />} Send email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
